import React, { useEffect, useState, useRef } from "react";
import { observer, inject } from "mobx-react";
import { langRegEx } from "constant/messageConstants";
import UnicodeNotification from "./UnicodeNotification";
import CustomButton from "components/AppButton/CustomButton/CustomButton";

const UnicodeDetector = props => {
  const { message, modalStore, onChange, isMms } = props;

  const [hasUnicodeInCurrentMessage, setHasUnicodeInCurrentMessage] = useState(false);
  const [isKnowMoreButton, setIsKnowMoreButton] = useState(false);

  const getIgnoredState = () => {
    try {
      return sessionStorage.getItem("unicodeIgnoredForSession") === "true";
    } catch (e) {
      return false;
    }
  };

  const setIgnoredState = ignored => {
    try {
      if (ignored) {
        sessionStorage.setItem("unicodeIgnoredForSession", "true");
      } else {
        sessionStorage.removeItem("unicodeIgnoredForSession");
      }
    } catch (e) {
      console.error("Something went wrong, Session storage not available");
    }
  };

  const [isIgnoredForCurrentSession, setIsIgnoredForCurrentSession] = useState(getIgnoredState());

  const messageChangeTimeout = useRef(null);

  const getModalShownState = () => {
    try {
      return sessionStorage.getItem("unicodeModalShownForSession") === "true";
    } catch (e) {
      return false;
    }
  };

  const setModalShownState = shown => {
    try {
      if (shown) {
        sessionStorage.setItem("unicodeModalShownForSession", "true");
      } else {
        sessionStorage.removeItem("unicodeModalShownForSession");
      }
    } catch (e) {
      console.error("Something went wrong, Session storage not available");
    }
  };

  const hasShownModalForCurrentSession = useRef(getModalShownState());

  const isGSMAlphabet = text => langRegEx.test(text);

  const hasUnicodeContent = text => {
    const trimmedMessage = (text || "").trim();
    return trimmedMessage !== "" && !isGSMAlphabet(text || "");
  };

  useEffect(() => {
    if (messageChangeTimeout.current) {
      clearTimeout(messageChangeTimeout.current);
    }

    messageChangeTimeout.current = setTimeout(() => {
      const currentMessage = message || "";
      const trimmedMessage = currentMessage.trim();
      const hasUnicode = hasUnicodeContent(currentMessage);
      setHasUnicodeInCurrentMessage(hasUnicode);

      if (trimmedMessage === "") {
        setIsIgnoredForCurrentSession(false);
        setIgnoredState(false);
        hasShownModalForCurrentSession.current = false;
        setModalShownState(false);
        return;
      }

      const currentIgnoredState = getIgnoredState();
      const currentModalShownState = getModalShownState();

      if (currentIgnoredState !== isIgnoredForCurrentSession) {
        setIsIgnoredForCurrentSession(currentIgnoredState);
      }

      if (currentModalShownState !== hasShownModalForCurrentSession.current) {
        hasShownModalForCurrentSession.current = currentModalShownState;
      }

      const shouldShow = hasUnicode && !isMms && !currentIgnoredState && !currentModalShownState;

      if (shouldShow) {
        hasShownModalForCurrentSession.current = true;
        setModalShownState(true);
        showUnicodeNotificationModal();
      }
    }, 50);

    return () => {
      if (messageChangeTimeout.current) {
        clearTimeout(messageChangeTimeout.current);
      }
    };
  }, [message, isMms, isIgnoredForCurrentSession]);

  useEffect(() => {
    return () => {
      if (messageChangeTimeout.current) {
        clearTimeout(messageChangeTimeout.current);
      }
    };
  }, []);

  const showUnicodeNotificationModal = (isManualClick = false) => {
    setIsKnowMoreButton(isManualClick);

    if (isManualClick) {
      modalStore.toggleModal("showUnicodeNotificationModal", true);
      return;
    }

    const currentIgnoredState = getIgnoredState();
    if (currentIgnoredState) {
      return;
    }

    modalStore.toggleModal("showUnicodeNotificationModal", true);
  };

  const handleIgnoreUnicode = () => {
    if (isKnowMoreButton) {
      modalStore.toggleModal("showUnicodeNotificationModal", false);
      setIsKnowMoreButton(false);
      return;
    }

    setIsIgnoredForCurrentSession(true);
    setIgnoredState(true);
    modalStore.toggleModal("showUnicodeNotificationModal", false);
  };

  return (
    <>
      {hasUnicodeInCurrentMessage && (
        <div className="mb-2">
          <span className="text-danger-color">Unicode Detected</span>
          <CustomButton
            label="Know More"
            onClick={async e => {
              e.preventDefault();
              showUnicodeNotificationModal(true);
            }}
            type="button"
            className="ms-2"
          />
        </div>
      )}

      {/* Always render UnicodeNotification, let it handle its own visibility */}
      <UnicodeNotification message={message} onChange={onChange} onIgnore={handleIgnoreUnicode} />
    </>
  );
};

export default inject(stores => ({
  modalStore: stores.store.modalStore
}))(observer(UnicodeDetector));
